import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { FormInput } from '../../../components/forms'
import { personnelService, type EmployeeProfile } from '../../../services'
import { useLocale } from '../../../hooks/use-locale'
import { Save, CheckCircle } from 'lucide-react'

export default function EmployeeAddressSection() {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['employee-profile', id],
    queryFn: () => personnelService.getEmployeeProfile(id!),
    enabled: !!id,
  })

  const [form, setForm] = useState<Partial<EmployeeProfile>>({})
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<EmployeeProfile>) =>
      personnelService.upsertEmployeeProfile(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-profile', id] })
      setSavedAt(new Date())
    },
  })

  const onSave = () => {
    if (!id) return
    saveMutation.mutate({
      addressStreet: form.addressStreet,
      addressPostalCode: form.addressPostalCode,
      addressCity: form.addressCity,
      addressCountry: form.addressCountry,
    })
  }

  return (
    <Card>
      <CardHeader className='flex-row items-center justify-between'>
        <div>
          <CardTitle>{t('common.address')}</CardTitle>
          <p className='text-sm text-neutral-500 mt-1'>
            {savedAt && (
              <span className='inline-flex items-center gap-1 text-success-600'>
                <CheckCircle className='w-3.5 h-3.5' />
                {t('common.savedAt', { date: formatDateTime(savedAt) })}
              </span>
            )}
          </p>
        </div>
        <Button onClick={onSave} variant='primary' loading={saveMutation.isPending} disabled={isLoading}>
          <Save className='w-4 h-4 mr-1.5' />
          {t('common.save')}
        </Button>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
          <FormInput
            label={t('common.street')}
            name='addressStreet'
            value={form.addressStreet || ''}
            onChange={onChange}
            placeholder={t('users.streetPlaceholder')}
          />
          <FormInput
            label={t('common.postalCode')}
            name='addressPostalCode'
            value={form.addressPostalCode || ''}
            onChange={onChange}
            placeholder={t('users.postalCodePlaceholder')}
          />
          <FormInput
            label={t('common.city')}
            name='addressCity'
            value={form.addressCity || ''}
            onChange={onChange}
            placeholder={t('users.cityPlaceholder')}
          />
          <FormInput
            label={t('common.country')}
            name='addressCountry'
            value={form.addressCountry || ''}
            onChange={onChange}
            placeholder={t('users.countryPlaceholder')}
          />
        </div>
      </CardContent>
    </Card>
  )
}
